import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useId } from "react";
import { Box, Chip, Popper, Paper, MenuItem, Typography, Fade, Tooltip, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { MultiSelectChipProps } from "./multi-select-chip-types";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";

export const MultiSelectChip: React.FC<MultiSelectChipProps> = ({ options: initialOptions, value, onChange, placeholder, label, tooltip, sx, darkMode = false }) => {
  const theme = useTheme();
  const [customOptions, setCustomOptions] = useState<string[]>([]);
  const options = [...initialOptions, ...customOptions.filter(o => !initialOptions.includes(o))].sort((a, b) => a.localeCompare(b));
  const [selected, setSelected] = useState<string[]>(value ?? []);
  const [inputVal, setInputVal] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [focusedChipIndex, setFocusedChipIndex] = useState<number>(-1);

  const genericTranslationsT = useGenericTranslationsT();
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const getOptionId = (i: number) => `${uid}-option-${i}`;

  const shrunk = isOpen || selected.length > 0 || inputVal.length > 0;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (value !== undefined) setSelected(value);
  }, [value]);

  useEffect(() => {
    if (!isOpen) setFocusedIndex(-1);
  }, [isOpen]);

  const filtered = options.filter(n => n.toLowerCase().includes(inputVal.trim().toLowerCase()));

  const isCustom: boolean = inputVal.trim().length > 0 && !options.map(n => n.toLowerCase()).includes(inputVal.trim().toLowerCase());

  const notifyChange = useCallback(
    (next: string[]) => {
      onChange?.(next);
    },
    [onChange]
  );

  // Scroll focused dropdown item into view without moving DOM focus
  useLayoutEffect(() => {
    const item = itemRefs.current[focusedIndex];
    const list = listRef.current;
    if (focusedIndex < 0 || !item || !list) return;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    if (itemTop < list.scrollTop) {
      list.scrollTop = itemTop;
    } else if (itemBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = itemBottom - list.clientHeight;
    }
  }, [focusedIndex]);

  const toggleOption = (name: string): void => {
    const next = selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name];
    setSelected(next);
    notifyChange(next);
    setInputVal("");
  };

  const addCustom = (val: string): void => {
    const trimmed = val.trim();
    if (!trimmed) return;
    const existing = options.find(n => n.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      if (!selected.includes(existing)) {
        const next = [...selected, existing];
        setSelected(next);
        notifyChange(next);
      }
    } else {
      setCustomOptions(prev => [...prev, trimmed]);
      const next = [...selected, trimmed];
      setSelected(next);
      notifyChange(next);
    }
    setInputVal("");
  };

  const removeChip = (name: string): void => {
    const next = selected.filter(n => n !== name);
    setSelected(next);
    notifyChange(next);
    setFocusedChipIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Tab") {
      e.preventDefault();
      if (inputVal.trim()) {
        addCustom(inputVal);
      } else if (focusedIndex >= 0 && filtered[focusedIndex]) {
        toggleOption(filtered[focusedIndex]);
        setInputVal("");
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      setFocusedIndex(i => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, -1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && filtered[focusedIndex]) {
        toggleOption(filtered[focusedIndex]);
        setInputVal("");
      } else if (inputVal.trim()) {
        addCustom(inputVal);
      }
      return;
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    // Arrow left from empty input: move focus into chips
    if (e.key === "ArrowLeft" && inputVal === "" && selected.length > 0) {
      e.preventDefault();
      setFocusedChipIndex(selected.length - 1);
      return;
    }
    if (e.key === "Backspace" && inputVal === "" && selected.length > 0) {
      const next = selected.slice(0, -1);
      setSelected(next);
      notifyChange(next);
    }
  };

  const handleChipKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, name: string, index: number): void => {
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      removeChip(name);
      const nextIndex = index > 0 ? index - 1 : -1;
      if (nextIndex >= 0) {
        setFocusedChipIndex(nextIndex);
      } else {
        inputRef.current?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (index > 0) setFocusedChipIndex(index - 1);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (index < selected.length - 1) {
        setFocusedChipIndex(index + 1);
      } else {
        setFocusedChipIndex(-1);
        inputRef.current?.focus();
      }
      return;
    }
    if (e.key === "Escape") {
      setFocusedChipIndex(-1);
      inputRef.current?.focus();
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setFocusedChipIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", mt: label ? 1 : 0, ...sx }}>
      {/* Input box — also serves as positioning context for both label and fieldset */}
      <Box
        onClick={() => {
          inputRef.current?.focus();
          setIsOpen(true);
        }}
        sx={{
          borderRadius: 1,
          px: 1.25,
          py: 1.25,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.75,
          cursor: "text",
          position: "relative",
          "& fieldset": {
            borderColor: darkMode ? theme.palette.customColors.whiteBorder : isOpen ? theme.palette.primary.main : theme.palette.customColors.border,
            transition: "border-color 0.15s"
          },
          "&:hover fieldset": {
            borderColor: darkMode ? theme.palette.customColors.whiteStrong : isOpen ? theme.palette.primary.main : theme.palette.text.secondary
          }
        }}
      >
        {/* Floating label — inside content box so top:0 is the same reference as the fieldset */}
        {label && (
          <Box
            sx={{
              position: "absolute",
              top: shrunk ? 4 : `calc(50% + ${theme.spacing(0.4)})`,
              left: shrunk ? 10 : 12,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              bgcolor: "transparent",
              pointerEvents: tooltip && shrunk ? "auto" : "none",
              transform: "translateY(-50%)",
              transition: "top 0.15s ease, font-size 0.15s ease, padding 0.15s ease"
            }}
          >
            <Typography
              component="label"
              htmlFor={`${uid}-input`}
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: shrunk ? theme.typography.caption.fontSize : theme.typography.body1.fontSize,
                color: darkMode ? theme.palette.primary.contrastText : shrunk && isOpen ? theme.palette.primary.main : theme.palette.text.secondary,
                lineHeight: 1,
                whiteSpace: "nowrap",
                transition: "font-size 0.15s ease, color 0.15s ease",
                cursor: "text"
              }}
            >
              {label}
            </Typography>
            {tooltip && shrunk && (
              <Tooltip title={tooltip} placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 14, color: theme.palette.text.disabled, cursor: "default" }} />
              </Tooltip>
            )}
          </Box>
        )}

        {/* Notched border — same technique as MUI OutlinedInput */}
        <Box
          component="fieldset"
          aria-hidden="true"
          sx={{
            textAlign: "left",
            position: "absolute",
            bottom: 0,
            right: 0,
            top: 0,
            left: 0,
            margin: 0,
            padding: theme.spacing(0, 1, 0, 0.5),
            pointerEvents: "none",
            minWidth: "0%",
            borderRadius: 1,
            borderWidth: 1,
            borderStyle: "solid"
          }}
        >
          {label && (
            <Box
              component="legend"
              sx={{
                float: "unset",
                width: "auto",
                overflow: "hidden",
                display: "block",
                padding: 0,
                height: 11,
                fontSize: "0.75em",
                visibility: "hidden",
                maxWidth: shrunk ? "100%" : 0,
                whiteSpace: "nowrap",
                transition: "max-width 100ms cubic-bezier(0,0,0.2,1) 50ms"
              }}
            >
              <span style={{ paddingLeft: 4, paddingRight: 4, display: "inline-block", opacity: 0, visibility: "visible" }}>{label}</span>
            </Box>
          )}
        </Box>

        {/* Chips */}
        {selected.map((name, index) => (
          <Chip
            key={name}
            label={name}
            size="small"
            tabIndex={focusedChipIndex === index ? 0 : -1}
            onKeyDown={e => handleChipKeyDown(e, name, index)}
            onFocus={() => setFocusedChipIndex(index)}
            onBlur={e => {
              if (!wrapperRef.current?.contains(e.relatedTarget as Node)) setFocusedChipIndex(-1);
            }}
            onDelete={() => removeChip(name)}
            onMouseDown={e => e.preventDefault()}
            sx={{
              height: 28,
              fontSize: theme.typography.caption.fontSize,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeightRegular,
              bgcolor: darkMode ? theme.palette.customColors.whiteChip : theme.palette.action.selected,
              color: darkMode ? theme.palette.primary.contrastText : theme.palette.text.primary,
              borderRadius: 1,
              outline: focusedChipIndex === index ? `2px solid ${darkMode ? theme.palette.primary.contrastText : theme.palette.primary.main}` : "none",
              "& .MuiChip-deleteIcon": {
                color: darkMode ? theme.palette.customColors.whiteStrong : theme.palette.text.secondary,
                fontSize: 16,
                "&:hover": { color: darkMode ? theme.palette.primary.contrastText : theme.palette.text.primary }
              }
            }}
          />
        ))}

        {/* Bare input */}
        <Box
          id={`${uid}-input`}
          component="input"
          ref={inputRef}
          value={inputVal}
          placeholder={selected.length === 0 ? placeholder : ""}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={focusedIndex >= 0 ? getOptionId(focusedIndex) : undefined}
          onFocus={() => setIsOpen(true)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setInputVal(e.target.value);
            setFocusedIndex(-1);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          sx={{
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.body1.fontSize,
            color: darkMode ? theme.palette.primary.contrastText : theme.palette.text.primary,
            minWidth: 80,
            flex: 1,
            p: 0,
            caretColor: theme.palette.text.primary,
            "&::placeholder": { color: theme.palette.text.disabled }
          }}
        />
      </Box>

      {/* Dropdown */}
      <Popper open={isOpen} anchorEl={wrapperRef.current} placement="bottom-start" transition style={{ width: wrapperRef.current?.offsetWidth, zIndex: 1300 }}>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Paper
              ref={listRef}
              elevation={4}
              role="listbox"
              id={listboxId}
              aria-label={label}
              aria-multiselectable="true"
              tabIndex={-1}
              sx={{
                mt: 0.5,
                borderRadius: 1,
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: theme.palette.divider,
                overflow: "hidden",
                maxHeight: 220,
                overflowY: "auto",
                outline: "none"
              }}
            >
              {/* "Press Tab to add" hint */}
              {isCustom && (
                <Box sx={{ px: 1.75, py: 0.875, borderBottomWidth: 1, borderBottomStyle: "solid", borderColor: theme.palette.divider }}>
                  <Typography variant="caption" color="text.disabled">
                    {genericTranslationsT.pressTabToAdd}&nbsp;<strong>{inputVal.trim()}</strong>
                  </Typography>
                </Box>
              )}

              {/* Empty state */}
              {filtered.length === 0 && !inputVal.trim() ? (
                <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 1.75 }}>
                  {genericTranslationsT.noOptionsAvailable}
                </Typography>
              ) : (
                filtered.map((name, i) => {
                  const isSelected = selected.includes(name);
                  const isFocused = i === focusedIndex;
                  return (
                    <MenuItem
                      key={name}
                      id={getOptionId(i)}
                      role="option"
                      aria-selected={isSelected}
                      ref={el => {
                        itemRefs.current[i] = el;
                      }}
                      onMouseDown={e => {
                        e.preventDefault();
                        toggleOption(name);
                      }}
                      selected={isFocused}
                      sx={{
                        px: 1.75,
                        py: 1,
                        fontSize: theme.typography.body2.fontSize,
                        fontWeight: isSelected ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: isFocused ? theme.palette.action.focus : "transparent",
                        "&.Mui-selected": {
                          bgcolor: isFocused ? theme.palette.action.focus : theme.palette.action.hover,
                          "&:hover": { bgcolor: theme.palette.action.focus }
                        },
                        "&:hover": {
                          bgcolor: theme.palette.action.hover
                        }
                      }}
                    >
                      {name}
                      {isSelected && <CheckIcon sx={{ fontSize: 14, color: theme.palette.text.primary }} />}
                    </MenuItem>
                  );
                })
              )}
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};
